import type { FxCurrency } from "@trader/shared/FxApi"

// The backtest's account currency (docs/design/0005, P1): which one a pane's
// strategy asks for, the FX range its bars need, the Properties options, and
// the words the tester uses. Plain functions, testable without React.

/** The FX rates for one run, as the worker takes them (numbers: the report is analysis, not the ledger). */
export interface RunFx {
  /** The account currency the report will be in. */
  readonly currency: string
  readonly source: string
  readonly derivedVia: string
  /** Units of `currency` per unit of the symbol's currency, fixed on `dates[i]` (ascending). */
  readonly dates: ReadonlyArray<string>
  readonly rates: ReadonlyArray<number>
}

/** A converted run failed for want of rates, and how it could be fixed. */
export interface CurrencyProblem {
  readonly message: string
  /** A bar time (epoch seconds) from which the backtest converts: "Start at". */
  readonly startAt: number | null
}

const CODE = /^[A-Z]{3}$/

/** The currency a strategy reports in: its explicit `properties.currency`, else the symbol's. */
export const accountCurrencyOf = (properties: Readonly<Record<string, unknown>>, symbolCurrency: string): string => {
  const chosen = properties["currency"]
  return typeof chosen === "string" && CODE.test(chosen) ? chosen : symbolCurrency
}

const dateFormats = new Map<string, Intl.DateTimeFormat>()

/** "YYYY-MM-DD" of an instant (epoch seconds) in a time zone: a bar's trading day. */
export const localDate = (time: number, timeZone: string): string => {
  let format = dateFormats.get(timeZone)
  if (format === undefined) {
    format = new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" })
    dateFormats.set(timeZone, format)
  }
  const parts = format.formatToParts(new Date(time * 1000))
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? ""
  return `${part("year")}-${part("month")}-${part("day")}`
}

/** The FX series a run needs: from its first bar's trading day to its last's (the server adds the fixing before). */
export const fxRange = (barTimes: ReadonlyArray<number>, timeZone: string): { readonly start: string; readonly end: string } | null => {
  const first = barTimes[0]
  const last = barTimes[barTimes.length - 1]
  return first === undefined || last === undefined ? null : { start: localDate(first, timeZone), end: localDate(last, timeZone) }
}

/** The first bar whose trading day is `day` or later (bars ascend), or null. */
export const firstBarFrom = (barTimes: ReadonlyArray<number>, timeZone: string, day: string): number | null => {
  let low = 0
  let high = barTimes.length
  while (low < high) {
    const middle = (low + high) >>> 1
    if (localDate(barTimes[middle]!, timeZone) < day) low = middle + 1
    else high = middle
  }
  return barTimes[low] ?? null
}

/** "05 Jan 2009" for a "YYYY-MM-DD" day, as the tester writes dates. */
export const formatDay = (day: string): string =>
  new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(`${day}T00:00:00Z`))

/** One choice in Properties › Account currency. */
export interface CurrencyOption {
  readonly code: string
  readonly label: string
}

/**
 * The Account currency choices: the symbol's own first ("INR (symbol currency)"),
 * then every currency the server has rates for. A currency without any rates is
 * left out; one whose rates are stale says so. While the list is loading (or if
 * it can't be loaded) the symbol's currency and the current choice are offered.
 */
export const currencyOptions = (
  symbolCurrency: string,
  currencies: ReadonlyArray<FxCurrency> | null,
  selected: string
): ReadonlyArray<CurrencyOption> => {
  const options: Array<CurrencyOption> = [{ code: symbolCurrency, label: `${symbolCurrency} (symbol currency)` }]
  const add = (code: string, label: string) => {
    if (!options.some((option) => option.code === code)) options.push({ code, label })
  }
  for (const currency of currencies ?? []) {
    if (currency.status === "missing") continue
    add(currency.code, currency.status === "stale" && currency.lastDate ? `${currency.code} (rates end ${formatDay(currency.lastDate)})` : currency.code)
  }
  if (CODE.test(selected)) add(selected, selected)
  return options
}

/** The tester's Currency row: "USD (converted from INR · ECB, previous-day rate)", or just the currency. */
export const reportCurrencyText = (report: {
  readonly currency: string
  readonly symbolCurrency?: string
  readonly fx?: { readonly source: string }
}): string => {
  if (report.fx === undefined || report.symbolCurrency === undefined || report.symbolCurrency === report.currency) return report.currency
  const source = report.fx.source === "ecb" ? "ECB" : report.fx.source
  return `${report.currency} (converted from ${report.symbolCurrency} · ${source}, previous-day rate)`
}

/** A tag and fields of the server's FxRateUnavailable, as the API client hands it over. */
export interface FxUnavailableDetail {
  readonly from: string
  readonly to: string
  readonly date: string
  readonly firstAvailable: string | null
}

const pair = (from: string, to: string) => `${from}→${to}`

/**
 * The server has no rate for the backtest's first day: say from when it does,
 * and offer the first bar from then on.
 */
export const unavailableProblem = (detail: FxUnavailableDetail, barTimes: ReadonlyArray<number>, timeZone: string): CurrencyProblem => {
  if (detail.firstAvailable === null) {
    return {
      message: `No ${pair(detail.from, detail.to)} exchange rate is available for ${formatDay(detail.date)} yet (ECB reference rates).`,
      startAt: null
    }
  }
  const startAt = firstBarFrom(barTimes, timeZone, detail.firstAvailable)
  return {
    message: `ECB reference rates convert ${detail.from} to ${detail.to} from ${formatDay(detail.firstAvailable)}; ` +
      (startAt === null ? "these bars all end before then." : `bars before then can't be converted.`),
    startAt
  }
}

/** A bar of the run has no usable rate (a gap, or the rates stopped more than a week before it). */
export const gapProblem = (
  gap: { readonly day: string; readonly firstCoveredBarTime: number | null },
  from: string,
  to: string
): CurrencyProblem => ({
  message: gap.firstCoveredBarTime === null
    ? `No ${pair(from, to)} exchange rate for ${formatDay(gap.day)} yet: the ECB rates stop more than a week before it.`
    : `No ${pair(from, to)} exchange rate for ${formatDay(gap.day)}: the ECB rates have a gap there.`,
  startAt: gap.firstCoveredBarTime
})

const FETCH_REASONS: Readonly<Record<string, string>> = {
  HttpClientError: "the server can't be reached",
  Unauthorized: "signed out",
  InvalidFxRange: "the backtest's dates are out of order"
}

/** The rates couldn't be fetched at all; `tag` is the API error's tag. */
export const fetchProblem = (from: string, to: string, tag: string): CurrencyProblem => ({
  message: `Couldn't load ${pair(from, to)} exchange rates: ${FETCH_REASONS[tag] ?? "something went wrong"}. Try again, or backtest in the symbol's currency.`,
  startAt: null
})
